import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, WritableSignal, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridComponent, GridDataResult, GridItem, GroupKey, PageChangeEvent, RowArgs } from '@progress/kendo-angular-grid';
import { TranslateService } from '@ngx-translate/core';
import { GenericEnumValueModel, WorkOrderBase, WorkOrderModel } from '../../../CommonFiles/BusinessModels';
import { AggregateDescriptor, CompositeFilterDescriptor, SortDescriptor, State } from '@progress/kendo-data-query';
import { Utils } from '../../../OFramework/utils';
import { gridIcon } from '@progress/kendo-svg-icons';

import {
  WOStatusOptions,
} from '../../../constants/woStatusOptions';
import {
  WOBlackout,
  GenericEnumValueString,
} from '../../../CommonFiles/enums/workOrderDetails.enum';
import { ColumnSettings, CustomColumnDescriptor } from '../../../CommonFiles/UIModels';
import { Subscription } from 'rxjs';
import { GenericEnumValueGRPCService } from '../../../ProxyServices/genericenumvalue.grpc.service';
import { ViewConfigGRPCService } from '../../../ProxyServices/view-config.grpc.service';
import { UserGRPCService } from '../../../ProxyServices/user.grpc.service';
import { Router } from '@angular/router';
import { ShowItem } from '../../home.component';
import { filterClearIcon } from "@progress/kendo-svg-icons";
import { isPiiField, getColumnCategory, getPiiGroupKeysFromColumns } from 'src/app/shared/pii-mask/pii-fields.config';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { PiiMaskService } from 'src/app/shared/services/pii-mask.service';

@Component({
  standalone: false,
  selector: 'app-kendo-grid',
  templateUrl: 'kendo-grid.component.html',
  styleUrls: ['kendo-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})

export class KendoGridComponent implements OnInit, OnDestroy {
  filterClearIcon = filterClearIcon;
  wOBlackout = WOBlackout;
  public readOnlyMode: boolean;
  gridState = input<State>();
  timezone = input<string>();
  pending = input<boolean>();
  workOrders = input<WorkOrderBase[]>([]);
  columnsList = input<ColumnSettings[]>([]);
  selectedWorkOrders = input<WorkOrderBase[]>([]);
  showItem = input(ShowItem.All);
  pageHeight = input<number>(600);
  selectedSet = input<Set<string>>();
  gridData = input<GridDataResult>({
    data: [],
    total: 0
  });

  isPiiRevealed = false;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  private readonly destroyRef = inject(DestroyRef);

  gridHeight = computed<number>(() => {
    const pageHeight = this.pageHeight();
    const gridTitleHeight = 52 * 2;
    return pageHeight - gridTitleHeight;
  });

  showConfirmationDialog: boolean = false;
  loading: WritableSignal<boolean> = signal(true);
  extraWidth: WritableSignal<number> = signal(0);

  gridWorkOrders: WritableSignal<WorkOrderBase[]> = signal([]);
  exportFileName: WritableSignal<string> = signal("wo.xlsx");

  groupGridData = computed<WorkOrderBase[]>(() => {
    const showItem = this.showItem();
    const workOrders = this.gridWorkOrders();
    let data = [];
    switch (showItem) {
      case ShowItem.Selected:
        const selectedSet = this.selectedSet();
        data = workOrders.filter((wo) => selectedSet.has(wo.name));
        break;
      case ShowItem.UnSelected: {
        const selectedSet = this.selectedSet();
        data = workOrders.filter((wo) => !selectedSet.has(wo.name));
        break;
      }
      default:
        data = workOrders;
        break;
    }
    return data;
  });
  columnsListFilter: CustomColumnDescriptor[] = [];
  localColumnList: ColumnSettings[] = [];

  @Output() updateState = new EventEmitter<State>();
  @Output() updateLocalState = new EventEmitter<State>();
  @Output() updateColumnsList = new EventEmitter<ColumnSettings[]>();
  @Output() updateWOSelection = new EventEmitter<WorkOrderBase[]>();
  @Output() updateShowItem = new EventEmitter<string>();
  @Output() clearFilters = new EventEmitter<void>();

  constructor(
    private _clientGenericEnumValue: GenericEnumValueGRPCService,
    public _clientUser: UserGRPCService,
    private ref: ChangeDetectorRef,
    public _clientViewConf: ViewConfigGRPCService, private router: Router,
    private piiService: PiiMaskService,
    private translate: TranslateService
  ) {
    this.setMenuItems();

    // Start listing to change in workOrders signal after initial load
    effect(() => {
      if (this.gridWorkOrders().length > 0) {
        this.gridWorkOrders.set(this.workOrders());
      }
    });
    // Start listing to change in workOrders signal after initial load
    effect(() => {
      const columnsList = this.columnsList();
      if (this.isColumnOption !== true) {
        // Update the local column list whenever parent list is changed
        this.localColumnList = columnsList;
      }
    });

  }

  aggregates: AggregateDescriptor[] = [
    { field: "Index", aggregate: "count" }
  ];

  public rowHeight: number = 35;//35 Work with the current CSS if it's not the good height the selection or scrolling is not working properly

  initiallyExpanded: boolean = false;
  public expandedGroupKeys: GroupKey[] = [];


  @ViewChild(GridComponent) public grid: GridComponent;
  @ViewChild("chkHeader") public selectAllCheckBox: ElementRef;
  genericEnumValueString = GenericEnumValueString;
  genericEnumMap: Map<string, GenericEnumValueModel>[] = [];
  statusWebList = WOStatusOptions;
  filterLocalInline: CompositeFilterDescriptor;
  menuItems = [];

  subscriptions: Subscription[] = [];

  selectedCallback = (args) => {
    return args.dataItem.name;
  };

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setMenuItems();
        this.ref.markForCheck();
      });

    const enumValueSub = this._clientGenericEnumValue.get().subscribe((value: GenericEnumValueModel[]) => {
      value.forEach((element) => (this.genericEnumMap[element.name] = element))
    });
    this.subscriptions.push(enumValueSub);

    // Derive icon state from service — no local timer needed
    this.piiService.groupStates$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const piiGroups = getPiiGroupKeysFromColumns(this.columnsList());
        this.isPiiRevealed = this.piiService.isAnyRevealed(piiGroups);
        this.ref.markForCheck();
      });

    // delay adding data to grid
    setTimeout(() => {
      this.loading.set(false);
      this.gridWorkOrders.set(this.workOrders());
      if (this.ref) {
        this.ref?.detectChanges();
      }
    }, 700);
  }

  private setMenuItems(): void {
    this.menuItems = [
      {
        text: this.translate.instant('HOME_COMPONENTS.KENDO_GRID.COLUMN_OPTIONS'),
        svgIcon: gridIcon,
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  onFilterChange(e) {
    const gridState: State = {
      ...this.gridState(),
      filter: e
    }
    this.updateLocalState.emit(gridState);
  }

  /* #region manage grid state on client side*/
  onSortChange(sort: SortDescriptor[]) {
    const gridState = {
      ...this.gridState(),
      sort
    }
    this.updateState.emit(gridState);

  }

  onReorder(e: any): void {
    // column virtual scroll is disabled because of the issue mentioned above
    const newIndex = e.newIndex;
    const field = e.column.field;
    const columnsList = [...this.columnsList()];
    const oldIndex = columnsList.findIndex(item => item.field === field);
    if (oldIndex > 0 && newIndex > 0) {
      // Remove the element from the original position
      const element = columnsList.splice(oldIndex, 1)[0];
      // Insert the element at the new position
      columnsList.splice(newIndex, 0, element);
      this.updateColumnsList.emit(columnsList);
      // https://github.com/telerik/kendo-angular/issues/3242
      setTimeout(() => {
        this.extraWidth.update((item) => {
          return item === 1 ? 0 : 1;
        });
      });
    } else {
      e.prevented = true;
    }
  }
  //TODO edson... should this be in the parent??
  onGridColResize(e: any): void {
    let columnsList = [...this.columnsList()];

    e.forEach((c) => {
      columnsList = columnsList.map((col) => {
        return {
          ...col,
          width: col.field === c.column.field ? c.newWidth : col.width
        }
      })
    });
    this.updateColumnsList.emit(columnsList);
  }

  isRowSelected = (e: RowArgs): boolean => {
    const isSelected = !!this.selectedSet().has(e.dataItem.name);
    return isSelected;
  };

  selectionChange(
    event:
      | {
        ctrlKey: boolean;
        shiftKey: boolean;
        selectedRows: { dataItem: WorkOrderModel; index: number }[];
        deselectedRows: { dataItem: WorkOrderModel; index: number }[];
      }
      | any
  ) {
    const selectedWorkOrders = Utils.selectionChange(event, this.selectedWorkOrders());
    this.updateWOSelection.emit(selectedWorkOrders);
  }

  onSelectAllChange() {
    const checked = this.selectAllCheckBox.nativeElement.checked;
    this.selectAllCheckBox.nativeElement.indeterminate = false;
    let selection = [];
    if (checked) {

      // when no filter is applied
      if (this.gridData().total === this.workOrders().length) {
        selection = [...this.workOrders()];
      } else {
        selection = [...this.gridData().data];
      }
    }
    this.updateWOSelection.emit(selection);
  }

  onShowChange(showItem: any): void {
    this.grid.scrollTo({ row: 0 }); // To return to the first item when the list change. ...?? Is there a better way to do it?

    // Bug 63307: Sometime large data set cause the issue where selected workorder filter is not working, settimeout fixes that issue.
    setTimeout(() => {
      this.updateShowItem.emit(showItem);
    }, 100);
  }

  pageChange(event: PageChangeEvent): void {
    const gridState = {
      ...this.gridState(),
      skip: event.skip
    };
    this.updateState.emit(gridState);
  }

  trackColumnsByField(index, col) {
    return col.field;
  }

  trackGridByName(index: number, item: GridItem) {
    const dataItem = item.data as WorkOrderBase;
    return dataItem.name;
  }

  isColumnOption: boolean = false;
  onSelectMenu(item): void {
    this.columnsListFilter = this.columnsList().map((col) => {
      return {
        field: col.field,
        title: col.title,
        checked: !col.hidden
      }
    });
    this.isColumnOption = true;
  }
  closeMenu() {
    this.isColumnOption = false;
    this.localColumnList = this.columnsList();
  }

  //TODO Move to framework edson make this generic... move the GEV to arch
  getStatus(value: string) {
    const lang: 'en' | 'fr' =
      this.translate.getCurrentLang() === 'fr' ? 'fr' : 'en';
    return lang == 'en'
      ? this.genericEnumMap[value]?.en_text
      : this.genericEnumMap[value]?.fr_text;
  }

  checkChangeColumnsConf(cc: CustomColumnDescriptor[]) {
    this.localColumnList = this.localColumnList.map(item => ({
      ...item,
      hidden: !cc.some(c => c.field === item.field && c.checked)
    }));
  }
  saveColumnsConf(cc: CustomColumnDescriptor[]) {
    const updatedColumnsList = this.columnsList().map(item => ({
      ...item,
      hidden: !cc.some(c => c.field === item.field && c.checked)
    }));
    this.updateColumnsList.emit(updatedColumnsList);
    this.isColumnOption = false;
  }

  public colorCodeforBG(dataItem) {
    let result = 'transparent';
    let inBlackOutWindow = dataItem?.InBlackout;
    if (inBlackOutWindow) {
      result = 'black';
    }
    return result;
  }

  public colorCodeforText(dataItem) {
    let result = 'black';
    let inBlackOutWindow = dataItem?.InBlackout;
    if (inBlackOutWindow) {
      result = 'white';
    } else {
      switch (dataItem?.BlackoutRemainingDays) {
        case 2:
        case -2:
          result = 'orange';
          break;
        case 1:
        case -1:
          result = 'red';
          break;
        default:
          result = 'black';
          break;
      }
    }

    return result;
  }


  public getBlackoutTextColorCode(dataItem) {
    const days = dataItem?.BlackoutRemainingDays;
    const inBlackout = dataItem?.InBlackout;
    const defaultColor = 'yellow';
    return !!(days > 0) ? inBlackout ? 'red' : 'green' : defaultColor;
  }

  detailsPageNavigation(name) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['details/' + name])
    );
    window.open(url, '_blank');
  }


  showConfirmation() {
    this.showConfirmationDialog = true;
  }
  confirm(value) {
    if (value === true) {
      this.clearFilters.emit();
    }
    this.showConfirmationDialog = false;
  }

  isPii(col: ColumnSettings): boolean {
    return isPiiField(col?.field, getColumnCategory(col));
  }

  /**
   * Resolve a column's value from a row, walking JsonData via the column's
   * `path` (e.g. `JsonData>Contact>CellPhoneNo`). Falls back to flat
   * `dataItem[col.field]` for non-JSON columns.
   */
  getCellValue(dataItem: any, col: ColumnSettings): any {
    if (!dataItem || !col) {
      return null;
    }
    if (col.isJson && col.path) {
      return col.path
        .split('>')
        .reduce<any>((acc, key) => (acc == null ? acc : acc[key]), dataItem);
    }
    return dataItem[col.field];
  }

  togglePii(): void {
    const piiGroups = getPiiGroupKeysFromColumns(this.columnsList());
    this.piiService.toggleGroups(piiGroups);
  }
}
